<template>
  <div class="pycad-tool-dropdown" ref="rootEl">
    <button
      ref="triggerRef"
      type="button"
      class="pycad-tool-btn pycad-tool-btn--labeled pycad-tool-dropdown__trigger"
      :class="{
        'pycad-tool-btn--active': isGroupActive,
        'pycad-tool-btn--disabled': disabled,
      }"
      :disabled="disabled"
      @click="toggleOpen"
    >
      <i :class="['mdi', displayIcon]" :style="{ fontSize: '17px' }"></i>
      <span class="pycad-tool-btn__caption pycad-tool-dropdown__caption">
        {{ displayCaption }}
        <i class="mdi mdi-chevron-down pycad-tool-dropdown__chevron"></i>
      </span>
    </button>

    <Teleport to="body">
      <transition name="pycad-dropdown-fade">
        <div
          v-if="open"
          ref="menuRef"
          class="pycad-tool-dropdown__menu"
          :style="menuStyle"
        >
          <div class="pycad-tool-dropdown__menu-title">{{ label }}</div>
          <button
            v-for="item in items"
            :key="item.tool"
            type="button"
            class="pycad-tool-dropdown__item"
            :class="{
              'pycad-tool-dropdown__item--active': activeTool === item.tool,
              'pycad-tool-dropdown__item--disabled': isItemDisabled(item),
            }"
            :disabled="isItemDisabled(item)"
            @click="onSelect(item)"
          >
            <i
              :class="['mdi', item.icon]"
              class="pycad-tool-dropdown__item-icon"
            ></i>
            <span class="pycad-tool-dropdown__item-label">{{
              item.label
            }}</span>
            <kbd
              v-if="item.shortcutAction && shortcut(item.shortcutAction)"
              class="pycad-kbd pycad-tool-dropdown__item-kbd"
            >
              {{ shortcut(item.shortcutAction) }}
            </kbd>
          </button>
        </div>
      </transition>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { onClickOutside } from '@vueuse/core';
import type { Tools } from '@/src/store/tools/types';
import type { ToolbarToolDef } from '@/src/features/types';
import { actionToKey } from '@/src/composables/useKeyboardShortcuts';
import { useFixedDropdownMenu } from '@/src/composables/useFixedDropdownMenu';

interface Props {
  /** Group name shown as caption when no group tool is active. */
  label: string;
  /** Icon shown when no group tool is active. */
  icon: string;
  items: ToolbarToolDef[];
  activeTool: Tools;
  disabled?: boolean;
  isItemDisabled?: (item: ToolbarToolDef) => boolean;
}

const props = withDefaults(defineProps<Props>(), {
  disabled: false,
  isItemDisabled: () => false,
});

const emit = defineEmits<{
  (e: 'select', tool: Tools): void;
}>();

const open = ref(false);
const rootEl = ref<HTMLElement>();
const triggerRef = ref<HTMLElement>();
const menuRef = ref<HTMLElement>();

const { menuStyle } = useFixedDropdownMenu(open, triggerRef);

onClickOutside(
  rootEl,
  () => {
    open.value = false;
  },
  { ignore: [menuRef] }
);

const activeItem = computed(() =>
  props.items.find((item) => item.tool === props.activeTool)
);
const isGroupActive = computed(() => !!activeItem.value);
const displayIcon = computed(() => activeItem.value?.icon ?? props.icon);
const displayCaption = computed(
  () => activeItem.value?.label.split(' ')[0] ?? props.label
);

function toggleOpen() {
  if (props.disabled) return;
  open.value = !open.value;
}

function onSelect(item: ToolbarToolDef) {
  emit('select', item.tool);
  open.value = false;
}

function shortcut(action: keyof typeof actionToKey.value): string {
  const k = actionToKey.value[action];
  if (!k) return '';
  return k.length === 1 ? k.toUpperCase() : k;
}
</script>

<style scoped>
.pycad-tool-dropdown {
  position: relative;
  display: inline-flex;
}

.pycad-tool-dropdown__caption {
  display: inline-flex;
  align-items: center;
  gap: 1px;
}

.pycad-tool-dropdown__chevron {
  font-size: 11px;
  opacity: 0.7;
}

.pycad-tool-dropdown__menu {
  min-width: 190px;
  padding: 6px;
  background: var(--pycad-elevated, #161616);
  border: 1px solid var(--pycad-border, #282828);
  border-radius: var(--pycad-radius, 10px);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.55);
}

.pycad-tool-dropdown__menu-title {
  padding: 4px 10px 6px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--pycad-text-muted, #666);
}

.pycad-tool-dropdown__item {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 8px 10px;
  font-size: 12.5px;
  font-family: inherit;
  color: var(--pycad-text-secondary, #b5b5b5);
  background: transparent;
  border: none;
  border-radius: var(--pycad-radius-sm, 6px);
  cursor: pointer;
  text-align: left;
  transition:
    background 0.12s ease,
    color 0.12s ease;
}

.pycad-tool-dropdown__item:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.06);
  color: var(--pycad-text-primary, #fff);
}

.pycad-tool-dropdown__item--active {
  color: var(--pycad-primary, #ff9900);
  background: var(--pycad-primary-dim, rgba(255, 153, 0, 0.12));
}

.pycad-tool-dropdown__item--active:hover:not(:disabled) {
  color: var(--pycad-primary, #ff9900);
  background: var(--pycad-primary-dim, rgba(255, 153, 0, 0.16));
}

.pycad-tool-dropdown__item--disabled {
  opacity: 0.4;
  cursor: default;
}

.pycad-tool-dropdown__item-icon {
  font-size: 15px;
  width: 18px;
  text-align: center;
}

.pycad-tool-dropdown__item-label {
  flex: 1;
  white-space: nowrap;
}

.pycad-tool-dropdown__item-kbd {
  flex-shrink: 0;
}

.pycad-dropdown-fade-enter-active,
.pycad-dropdown-fade-leave-active {
  transition:
    opacity 0.12s ease,
    transform 0.12s ease;
}

.pycad-dropdown-fade-enter-from,
.pycad-dropdown-fade-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}
</style>
